/**
 * `/` — Home, where people land after signing in (ADR-0070): what is waiting
 * on them and what they were working on. Their profile stays at `/me`.
 *
 * Two columns (2:1) that stack on a narrow screen: on the right, who you are
 * signed in as, your tools, and — for Developers and Administrators — your
 * collections. Everything shown comes from reads the person's own pages
 * already make, scoped by the server; Home adds no permission of its own and
 * writes nothing.
 */

import { Grid, Stack, Text, Title } from "@mantine/core";
import { useEffectiveRoles } from "@/features/auth/RolePreviewContext";
import { useMe } from "@/features/users/useMe";
import { CollectionsCard } from "./CollectionsCard";
import { dateLine, firstName, greeting, waitingLine } from "./homeText";
import { ProfileCard } from "./ProfileCard";
import { RecentCard } from "./RecentCard";
import { ToolsCard } from "./ToolsCard";
import { useWaiting } from "./useWaiting";
import { WaitingCard } from "./WaitingCard";

export function HomePage() {
  const { data: me } = useMe();
  const { items, failed } = useWaiting();
  const now = new Date();
  const name = firstName(me?.display_name);
  const count = waitingLine(items === null ? null : items.length);
  // The effective roles honour an Administrator's "view as role" preview, as
  // the sidebar does.
  const roles = useEffectiveRoles(me?.roles ?? []);
  const isAdmin = roles.includes("administrator");
  const isDeveloperPlus = isAdmin || roles.includes("developer");

  return (
    <Stack gap="lg">
      <div>
        <Title order={2}>
          {greeting(now)}
          {name ? `, ${name}` : ""}
        </Title>
        <Text c="dimmed" size="sm">
          {dateLine(now)}
          {count ? ` · ${count}` : ""}
        </Text>
      </div>
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="lg">
            <WaitingCard items={items} failed={failed} />
            <RecentCard me={me ?? undefined} />
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="lg">
            {me && <ProfileCard me={me} />}
            {me && (
              <ToolsCard
                agents={me.agents}
                isDeveloperPlus={isDeveloperPlus}
                isAdmin={isAdmin}
                offered={me.deployment_solutions}
              />
            )}
            {isDeveloperPlus && <CollectionsCard />}
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
